import "./SpacesPage.scss";

import { Button, ButtonIconPosition, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip, ChipBackgroundColor, ChipIconPosition } from "@amzn/stencil-react-components/chip";
import { Expander, ExpanderChevronPosition } from "@amzn/stencil-react-components/expander";
import { Col, GridContainer, Row } from "@amzn/stencil-react-components/layout";
import { RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { PaginationVariant } from "@amzn/stencil-react-components/pagination";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import {
  IconBinSmall,
  IconLayersSmall,
  IconPersonSmall,
  IconSettingsSmall,
  IconShareSmall,
  IconShowSmall,
} from "@amzn/stencil-react-icons";
import { Pagination } from "@amzn/uxm-stencil-react-components/lib/src/stencil";
import { AiForMeItem } from "common/components/AIForMe/AiForMeItem";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { SpacePageHeader } from "common/components/Spaces/SpacePageHeader";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { AIForMeTool } from "lib/models/aiforme-models";
import { CatalogicalService, UserPermissions } from "lib/services";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { EditSpaceModal } from "./EditSpaceModal";

// ── Mock data ──

interface MockList {
  id: string;
  name: string;
  items: AIForMeTool[];
}

interface SpaceSection {
  title: string;
  position: number;
  listIds: string[];
  itemIds: string[];
}

interface SpacePageData {
  spaceAlias: string;
  owner: string;
  title: string;
  description: string;
  domain: string;
  visibility: string;
  active: boolean;
  sections: Record<string, SpaceSection>;
}

const makeMockTool = (id: string, name: string, rating: number, views: number, description: string): AIForMeTool =>
  ({
    taxonomyId: `atn::aiforme/tools/${id}`,
    name,
    title: name,
    description,
    status: "approved",
    jobFamilies: [],
    creationDate: "2026-01-01T00:00:00Z",
    categories: ["AI Tools"],
    learnMoreLink: "#",
    tags: [],
    recommended: false,
    dataClassification: "public",
    feedback: {
      averageRating: rating,
      numberOfFeedback: views,
      numberOfRatings: views,
      ratingCounts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
    },
    sortingPriority: "none",
  } as unknown as AIForMeTool);

const MOCK_TOOLS: AIForMeTool[] = [
  makeMockTool(
    "quick-suite",
    "Amazon Quick Suite",
    4.39,
    6000,
    "Amazon Quick Suite is a unified experience that combines AI agents for deep research, business insights, and workflow.."
  ),
  makeMockTool("cline", "Cline", 4.71, 6500, "Internal fork of Cline, the autonomous coding agent"),
  makeMockTool(
    "q-dev-cli",
    "Q Developer CLI",
    4.56,
    6500,
    "A conversational assistant that can help you understand, build, extend, and operate AWS applications."
  ),
  makeMockTool(
    "aza",
    "AZA (A to Z Assistant)",
    4.13,
    4700,
    "Integrates generative AI capabilities with direct access to internal Amazon knowledge, tools, and resources."
  ),
  makeMockTool(
    "aims",
    "AI-generated Meeting Summary (AiMS)",
    4.37,
    2400,
    "Amazon's meeting summarization solution for generating meeting summaries using AI."
  ),
  makeMockTool(
    "amazon-q",
    "Amazon Q",
    4.08,
    8500,
    "General AI assistant for your enterprise data and/or operating AWS applications."
  ),
  makeMockTool("kiro", "Kiro IDE", 4.65, 3200, "AI-powered development environment for Amazon builders."),
  makeMockTool(
    "bedrock",
    "Amazon Bedrock",
    4.42,
    7100,
    "Fully managed service for building generative AI applications with foundation models."
  ),
  makeMockTool("sagemaker", "SageMaker", 4.28, 5400, "Build, train, and deploy machine learning models at scale."),
  makeMockTool(
    "codewhisperer",
    "CodeWhisperer",
    4.51,
    9200,
    "AI-powered code suggestions and security scanning for developers."
  ),
  makeMockTool(
    "rufus",
    "Rufus",
    4.19,
    2800,
    "AI shopping assistant that helps customers find products and make purchase decisions."
  ),
  makeMockTool("honeycode", "Honeycode", 3.95, 1500, "Build mobile and web apps without writing code."),
];

const MOCK_LISTS: Record<string, MockList> = {
  "lst-ux-design": { id: "lst-ux-design", name: "UX Design and Prototyping tools", items: MOCK_TOOLS },
  "lst-ux-research": { id: "lst-ux-research", name: "UX Research tools", items: MOCK_TOOLS.slice(0, 4) },
};

const MOCK_SPACE: SpacePageData = {
  spaceAlias: "gavindas",
  owner: "gavindas",
  title: "IT Pnd UX",
  description:
    "This team space brings together AI tools curated for UX designers and researchers. Explore tools for prototyping, user research, design documentation, and more — all in one place.",
  domain: "aiforme",
  visibility: "Public",
  active: true,
  sections: {
    s1: { title: "Design Tools", position: 0, listIds: ["lst-ux-design", "lst-ux-research"], itemIds: [] },
  },
};

const MOCK_CURRENT_USER = "gavindas";
const TOOLS_PER_PAGE = 6;

// No-op mocks for AiForMeItem required props
const noopVoteHandler = async () => false;
const noopTrackClick = () => {};
const noopCompareAdd = () => {};
const noopCompareRemove = () => {};

// ── List Section (collapsible with carousel) ──

const ListSection: FC<{
  list: MockList;
  client: CatalogicalService<AIForMeTool>;
  isOwner: boolean;
  onRemove?: () => void;
}> = ({ list, client, isOwner, onRemove }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(list.items.length / TOOLS_PER_PAGE);
  const startIndex = (currentPage - 1) * TOOLS_PER_PAGE;
  const displayedTools = list.items.slice(startIndex, startIndex + TOOLS_PER_PAGE);

  const renderRemoveButton = () => {
    if (!isOwner || !onRemove) return null;
    return (
      <WithModal
        renderModal={({ close }: RenderModalFunctionParams) => (
          <Col padding="dimensions.spacing.500" gridGap="dimensions.spacing.400" style={{ maxWidth: 400 }}>
            <Text fontSize="T300" fontWeight="bold">
              {getString("spaces.page.removeList")}
            </Text>
            <Text>{getString("spaces.page.removeListConfirm", { name: list.name })}</Text>
            <Row gridGap="dimensions.spacing.200" justifyContent="flex-end">
              <Button variant={ButtonVariant.Secondary} onClick={close}>
                {getString("spaces.create.cancel")}
              </Button>
              <Button
                variant={ButtonVariant.Primary}
                onClick={() => {
                  onRemove();
                  close();
                }}
              >
                {getString("spaces.page.remove")}
              </Button>
            </Row>
          </Col>
        )}
      >
        {({ open }) => (
          <Button variant={ButtonVariant.Tertiary} onClick={open}>
            <IconBinSmall color="color.red.70" title="Remove list" />
          </Button>
        )}
      </WithModal>
    );
  };

  return (
    <Expander
      titleText={list.name}
      headingLevel={3}
      chevronPosition={ExpanderChevronPosition.Leading}
      shouldExpandOnMount
      renderTitle={(params) => (
        <Row alignItems="center" gridGap="dimensions.spacing.200" {...params}>
          <Text fontWeight="bold">{list.name}</Text>
          {renderRemoveButton()}
        </Row>
      )}
    >
      <Col gridGap="dimensions.spacing.300">
        <GridContainer>
          {displayedTools.map((tool, index) => (
            <AiForMeItem
              key={tool.taxonomyId}
              item={tool}
              index={startIndex + index}
              voteHandler={noopVoteHandler}
              client={client}
              gridSpan={{ lg: 4, md: 4 }}
              isCompareSelected={false}
              isCompareDisabled={true}
              onCompareAdd={noopCompareAdd}
              onCompareRemove={noopCompareRemove}
              onTrackClick={noopTrackClick}
              hideCompare={true}
            />
          ))}
        </GridContainer>
        {totalPages > 1 && (
          <Row justifyContent="center">
            <Pagination
              numberOfPages={totalPages}
              selectedPage={currentPage}
              onPageSelect={setCurrentPage}
              variant={PaginationVariant.Dotted}
              touchpoint={`spaces-list-pagination-${list.id}`}
            />
          </Row>
        )}
      </Col>
    </Expander>
  );
};

const SpacesPage: FC = () => {
  const { spaceAlias } = useParams<{ spaceAlias: string }>();
  const history = useHistory();
  const [space, setSpace] = useState<SpacePageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [gradient, setGradient] = useState({ left: "#f2f9ff", right: "#F3EFFA" });
  const catalogicalClient = useMemo(() => new CatalogicalService<AIForMeTool>("aiforme"), []);

  let hasFeature = false;
  try {
    hasFeature = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_AIFORME_SPACES);
  } catch {
    hasFeature = false;
  }
  const isOwner = space?.owner === MOCK_CURRENT_USER;

  const handleSaveEdit = (data: { title: string; description: string; gradient: { left: string; right: string } }) => {
    if (!space) return;
    setSpace({ ...space, title: data.title, description: data.description });
    setGradient(data.gradient);
  };

  const handleRemoveList = (sectionId: string, listId: string) => {
    if (!space) return;
    const section = space.sections[sectionId];
    if (!section) return;
    setSpace({
      ...space,
      sections: {
        ...space.sections,
        [sectionId]: { ...section, listIds: section.listIds.filter((id) => id !== listId) },
      },
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (spaceAlias === MOCK_SPACE.spaceAlias) {
        setSpace(MOCK_SPACE);
      } else {
        history.replace("/");
      }
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [spaceAlias, history]);

  const sortedSections = useMemo(
    () => (space ? Object.entries(space.sections).sort(([, a], [, b]) => a.position - b.position) : []),
    [space]
  );

  const totalTools = useMemo(() => {
    return sortedSections.reduce((total, [, section]) => {
      return (
        total +
        section.listIds.reduce((sum, listId) => {
          const list = MOCK_LISTS[listId];
          return sum + (list?.items.length || 0);
        }, 0)
      );
    }, 0);
  }, [sortedSections]);

  if (!hasFeature) {
    history.replace("/aiforme");
    return null;
  }
  if (isLoading)
    return (
      <Col alignItems="center" justifyContent="center" minHeight="400px">
        <Spinner size={SpinnerSize.Small} />
      </Col>
    );
  if (!space) return null;

  const renderOwnerActions = () => {
    if (!isOwner || !space) return null;
    return (
      <Row alignItems="center" gridGap="dimensions.spacing.200">
        <Button
          variant={ButtonVariant.Primary}
          icon={<IconShareSmall title="Share" />}
          iconPosition={ButtonIconPosition.Trailing}
        >
          {getString("spaces.page.shareUrl")}
        </Button>
        <EditSpaceModal
          trigger={(open) => (
            <Button
              variant={ButtonVariant.Secondary}
              icon={<IconSettingsSmall title="Settings" />}
              iconPosition={ButtonIconPosition.Trailing}
              onClick={open}
            >
              {getString("spaces.page.editSettings")}
            </Button>
          )}
          title={space.title}
          description={space.description}
          gradient={gradient}
          onSave={handleSaveEdit}
        />
      </Row>
    );
  };

  const renderMetadataChips = () => {
    if (!space) return null;
    return (
      <Row alignItems="center" gridGap="dimensions.spacing.200" flexWrap="wrap">
        <Chip
          icon={<IconLayersSmall />}
          iconPosition={ChipIconPosition.Leading}
          backgroundColor={ChipBackgroundColor.Blue}
        >
          {getString("spaces.page.tools", { count: String(totalTools) })}
        </Chip>
        <Chip
          icon={<IconShowSmall />}
          iconPosition={ChipIconPosition.Leading}
          backgroundColor={ChipBackgroundColor.Green}
        >
          {space.visibility}
        </Chip>
        <Chip
          icon={<IconPersonSmall />}
          iconPosition={ChipIconPosition.Leading}
          backgroundColor={ChipBackgroundColor.Purple}
        >
          {getString("spaces.page.owner", { name: space.owner })}
        </Chip>
      </Row>
    );
  };

  const renderSections = () => (
    <>
      {sortedSections.map(([sectionId, section]) => (
        <Col key={sectionId} gridGap="dimensions.spacing.300" data-testid={`section-${sectionId}`}>
          {section.listIds.map((listId) => {
            const list = MOCK_LISTS[listId];
            if (!list) return null;
            return (
              <ListSection
                key={listId}
                list={list}
                client={catalogicalClient}
                isOwner={isOwner}
                onRemove={() => handleRemoveList(sectionId, listId)}
              />
            );
          })}
        </Col>
      ))}
    </>
  );

  return (
    <Col>
      <SpacePageHeader
        title={space.title}
        description={space.description}
        gradient={gradient}
        breadcrumbs={[
          { title: getString("logoText"), url: "/" },
          { title: "AI for Me", url: "/aiforme" },
          { title: getString("spaces.explore.title"), url: "/aiforme/spaces" },
          { title: space.title },
        ]}
      >
        {renderOwnerActions()}
      </SpacePageHeader>

      <PageMainContainer>
        <Col gridGap="dimensions.spacing.400" margin={{ top: "dimensions.spacing.400" }}>
          {renderMetadataChips()}
          {renderSections()}
        </Col>
      </PageMainContainer>
    </Col>
  );
};

export default SpacesPage;
